import { message } from 'antd';
import { QueryClient } from 'react-query';
import { put, select, takeLatest } from 'redux-saga/effects';
import { Automation, AutomationType } from 'api/automations';
import { CustomAutomationsApi } from 'api/automations/custom-automation-api';
import { Role } from 'api/models';
import {
  PlanningLevelConfig,
  ResourceManagementConfig,
} from 'api/resource-management/types';
import { FilterableField } from 'components/organisms/tasks/fields';
import { RootState } from 'data';
import { StoreActionProps } from 'data/core';
import { getQueryClientContext } from 'data/core/sagas/query-client';
import { setDirtyState } from 'data/templates';
import {
  TemplateId,
  TemplateObjectType,
  TemplatesAPI,
} from 'data/templates/api';
import {
  deleteTemplateFromIndexedDb,
  getTemplateDraftFromIndexedDb,
} from 'hooks/use-idb';
import { getResourceManagementConfigQueryKey } from 'queries/resource-management-configuration';
import {
  ROLES_QUERY_KEY,
  ROLES_WITH_PLACEHOLDER_QUERY_KEY,
} from 'queries/roles';
import { logger } from 'services';
import { TeamsStore } from 'stores/teams';
import { migrations } from 'workers/migrations';
import { TemplateDraft } from 'workers/models/project-templates';
import {
  fetchProjectTemplateStart,
  fetchProjectTemplateStartSuccess,
} from '..';
import { fetchProjectTemplate } from '../actions';
import { getDynamicConditionsFromPayload } from '../api/transform';
import { getProjectTemplatePhaseIds } from '../selectors';
import {
  setDynamicConditions,
  setProjectTemplateActiveRecord,
} from '../slices';
import { ProjectTemplateApi } from '../types';
import { generateNewTaskRows, hoursPerWeekModifierRedux } from './utils';

const isTemplateProjectTemplate = (data: any): data is ProjectTemplateApi =>
  (data as any)?.objectType === TemplateObjectType.Project;

function* fetchProjectTemplateSaga(
  action: StoreActionProps<{
    templateId: number;
    dynamicConditionFields: FilterableField[];
    data: ProjectTemplateApi;
  }>,
) {
  const queryClient = (yield getQueryClientContext()) as QueryClient;

  try {
    const { payload } = action;
    const { templateId, dynamicConditionFields } = payload;

    yield put(fetchProjectTemplateStart({ templateId }));
    const automations: Automation[] =
      yield CustomAutomationsApi.getAllAutomationsForParams({
        automationType: AutomationType.CustomAutomation,
        projectTemplateId: templateId,
      });

    const templateData = yield TemplatesAPI.getById<ProjectTemplateApi>(
      templateId,
    );
    const templateDataWithAutomations = { ...templateData, automations };

    let data: ProjectTemplateApi =
      migrations.addEntityOrderToProjectTemplatePhases(
        templateDataWithAutomations,
      );

    const templateFromDbPromise = getTemplateDraftFromIndexedDb(templateId);
    const automationIdsFromBE = automations.map((e) => e.automationId);

    const dataFromIdbWithoutPreProcessing: TemplateDraft =
      yield templateFromDbPromise;
    let content = dataFromIdbWithoutPreProcessing?.content;

    if (content !== undefined && isTemplateProjectTemplate(content)) {
      content = {
        ...content,
        automations: content?.automations
          ? content?.automations.filter((e) =>
              e.automationId < 0
                ? true
                : automationIdsFromBE.includes(e.automationId),
            )
          : [],
      };
    }
    const dataFromIdb =
      dataFromIdbWithoutPreProcessing === null ||
      dataFromIdbWithoutPreProcessing === undefined
        ? null
        : { ...dataFromIdbWithoutPreProcessing, content };

    if (
      dataFromIdb != null &&
      dataFromIdb.content.objectType === TemplateObjectType.Project
    ) {
      if (dataFromIdb.timestamp > (data.publishedAt ?? data.createdAt ?? 0)) {
        data = dataFromIdb.content;
        yield put(
          setDirtyState({
            id: templateId,
            _state: true,
          }),
        );
      } else {
        const arrayOfDocIds: TemplateId[] = [];

        dataFromIdb.content.templateContent.spaceList.forEach((x) => {
          x.spaceTabs.forEach((y) => {
            arrayOfDocIds.push(y.spaceTabId);
          });
        });

        arrayOfDocIds.push(templateId);
        yield deleteTemplateFromIndexedDb(arrayOfDocIds);
      }
    }
    const rmConfig = queryClient.getQueryData<ResourceManagementConfig>(
      getResourceManagementConfigQueryKey(),
    );

    const newData = hoursPerWeekModifierRedux(
      data,
      rmConfig?.planningLevelConfig === PlanningLevelConfig.HoursPerWeek,
    );

    const dataWithNewTaskRows = generateNewTaskRows(newData);

    const rolesWithPlaceholder = queryClient.getQueryData<Role[]>(
      ROLES_WITH_PLACEHOLDER_QUERY_KEY,
    );
    const roles = queryClient.getQueryData<Role[]>(ROLES_QUERY_KEY);
    const { byId: teamsById }: TeamsStore = yield select(
      ({ teams }: RootState) => teams,
    );

    yield put(
      fetchProjectTemplateStartSuccess({
        template: dataWithNewTaskRows,
        rolesWithPlaceholder,
        allRoles: roles,
        teamsLookup: teamsById,
      }),
    );

    const phaseIds = yield select(getProjectTemplatePhaseIds(templateId));

    if (phaseIds[0]) {
      yield put(
        setProjectTemplateActiveRecord({
          id: phaseIds[0],
          type: 'PROJECT_PHASE',
        }),
      );
    }

    const dynamicConditions = getDynamicConditionsFromPayload(
      data.templateContent,
      dynamicConditionFields,
    );

    yield put(setDynamicConditions(dynamicConditions));
  } catch (error) {
    logger.error('an error fetching the project template', { error });

    message.error('There was an error fetching the project template');
  }
}

export function* watchFetchProjectTemplateSaga(): Generator {
  yield takeLatest(fetchProjectTemplate.type, fetchProjectTemplateSaga);
}
